import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Calendar, ChevronLeft, ChevronRight, Search, Save, RotateCcw, ChevronDown, ChevronUp,
  Palmtree, Stethoscope, CalendarX, Copy, ClipboardCopy, Ban,
  CheckCircle2, AlertTriangle, Circle, Info, CalendarClock,
  Clock3, SunMedium, Moon, MoreVertical,
} from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const weekStartFor = (iso) => { // Mon-start week
  const d = new Date(iso); const dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
};
const fmtRange = (start) => {
  const s = new Date(start); const e = new Date(start); e.setDate(e.getDate() + 6);
  const opts = { day: "numeric", month: "short" };
  return `${s.toLocaleDateString("en-GB", opts)} – ${e.toLocaleDateString("en-GB", { ...opts, year: "numeric" })}`;
};
const fmtFullDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const initials = (name) => (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

function diffHours(timeIn, timeOut, breakMin) {
  if (!timeIn || !timeOut) return 0;
  const [hi, mi] = timeIn.split(":").map(Number);
  const [ho, mo] = timeOut.split(":").map(Number);
  const mins = (ho * 60 + mo) - (hi * 60 + mi) - Number(breakMin || 0);
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

const STATUS_OPTS = [
  { v: "Working", tone: "text-emerald-700 border-emerald-200 bg-emerald-50" },
  { v: "On Holiday", tone: "text-blue-700 border-blue-200 bg-blue-50" },
  { v: "Sick Leave", tone: "text-violet-700 border-violet-200 bg-violet-50" },
  { v: "Absent", tone: "text-red-700 border-red-200 bg-red-50" },
  { v: "Not working", tone: "text-muted-foreground border-border bg-muted/40" },
];
const toneFor = (s) => STATUS_OPTS.find((o) => o.v === s)?.tone || "";

// Per-row save state
const SAVE_TONE = {
  saved: "text-emerald-700 border-emerald-200 bg-emerald-50",
  needs_review: "text-amber-700 border-amber-200 bg-amber-50",
  missing: "text-red-700 border-red-200 bg-red-50",
  dirty: "text-blue-700 border-blue-200 bg-blue-50",
  empty: "text-muted-foreground border-border bg-muted/40",
};
const SAVE_LABEL = { saved: "Saved", needs_review: "Needs review", missing: "Missing", dirty: "Unsaved", empty: "Not logged" };

const SOURCE_LABEL = {
  employee_shift_preset: "From employee shift",
  employee_custom_shift: "From custom hours",
  employee_standard_daily_hours: "From standard daily hours",
  calculated_from_contract: "Calculated from contract",
  company_standard_shift: "Company standard shift",
  system_fallback: "System fallback",
  standard_shift_autofill: "Auto-filled standard",
  manual: "Manually entered",
  clock_in: "Clock-in record",
};

export default function QuickHoursPage() {
  // Filters
  const [date, setDate] = useState(todayStr());
  const [weekStart, setWeekStart] = useState(() => weekStartFor(todayStr()));
  const [dept, setDept] = useState("all");
  const [emptype, setEmptype] = useState("all");
  const [search, setSearch] = useState("");
  const [fastEntry, setFastEntry] = useState(false);

  // Data
  const [employees, setEmployees] = useState([]);
  const [serverEntries, setServerEntries] = useState({}); // employee_id -> time entry
  const [serverHolidays, setServerHolidays] = useState({}); // employee_id -> holiday entry
  const [serverSick, setServerSick] = useState({}); // employee_id -> sick leave entry
  const [weeklyRows, setWeeklyRows] = useState([]); // /time-entries/weekly
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState({
    standard: { label: "Usual 9–5:30", time_in: "09:00", time_out: "17:30", break_minutes: 30 },
    early:    { label: "Early",        time_in: "07:00", time_out: "15:00", break_minutes: 30 },
    late:     { label: "Late",         time_in: "13:00", time_out: "21:00", break_minutes: 30 },
  });

  // Local draft: employee_id -> { status, time_in, time_out, break_minutes, notes, entry_id? }
  const [draft, setDraft] = useState({});
  // Selection
  const [selected, setSelected] = useState(() => new Set());
  // Collapsed departments
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Right panel selected employee
  const [summaryEmpId, setSummaryEmpId] = useState("");
  // Collapse the weekly-summary sidebar (desktop)
  const [summaryCollapsed, setSummaryCollapsed] = useState(() => localStorage.getItem("qh:summaryCollapsed") === "1");

  // ---- Load ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dept !== "all") params.department = dept;
      if (emptype !== "all") params.employment_type = emptype;
      params.status = "active";
      const [empsRes, timeRes] = await Promise.all([
        api.get("/employees", { params }),
        api.get("/time-entries", { params: { start: date, end: date } }),
      ]);
      setEmployees(empsRes.data);
      const t = {}; for (const e of timeRes.data) t[e.employee_id] = e;
      setServerEntries(t);
      // Holidays + sick for that date (best-effort, filter client-side)
      try {
        const year = Number(date.slice(0, 4));
        const h = await api.get("/holidays/entries", { params: { year } });
        const map = {}; for (const x of h.data || []) if (x.date === date) map[x.employee_id] = x;
        setServerHolidays(map);
      } catch { setServerHolidays({}); }
      try {
        const sk = await api.get("/sick-leave");
        const map = {};
        for (const x of sk.data || []) if (x.start_date <= date && x.end_date >= date && x.status !== "rejected" && x.status !== "cancelled") map[x.employee_id] = x;
        setServerSick(map);
      } catch { setServerSick({}); }
      // Weekly totals
      try {
        const w = await api.get("/time-entries/weekly", { params: { week_start: weekStart } });
        setWeeklyRows(w.data.rows || []);
      } catch { setWeeklyRows([]); }
      // Shift presets from settings (only admin can see, but GET /settings works for any logged-in manager+)
      try {
        const s = await api.get("/settings");
        if (s.data?.shift_presets) setPresets(s.data.shift_presets);
      } catch { /* fall back to defaults */ }

      // Seed draft from server for each employee if not already dirty
      setDraft((prev) => {
        const next = { ...prev };
        for (const emp of empsRes.data) {
          if (next[emp.id]?.dirty) continue; // keep user's unsaved edits
          const te = t[emp.id];
          next[emp.id] = te
            ? { status: "Working", time_in: te.time_in, time_out: te.time_out, break_minutes: te.break_minutes, notes: te.notes || "", entry_id: te.id, hours_source: te.hours_source, dirty: false }
            : { status: "Not working", time_in: "", time_out: "", break_minutes: 30, notes: "", entry_id: null, hours_source: null, dirty: false };
        }
        return next;
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [date, dept, emptype, weekStart]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { setWeekStart(weekStartFor(date)); }, [date]);

  // ---- Filters derived ----
  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => set.add(e.department || "Unassigned"));
    return Array.from(set).sort();
  }, [employees]);

  const visibleEmps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => !q || (e.full_name || "").toLowerCase().includes(q) || (e.employee_id || "").toLowerCase().includes(q));
  }, [employees, search]);

  const grouped = useMemo(() => {
    const g = {};
    visibleEmps.forEach((e) => {
      const k = e.department || "Unassigned";
      (g[k] ||= []).push(e);
    });
    // stable sort inside each
    Object.values(g).forEach((arr) => arr.sort((a, b) => a.full_name.localeCompare(b.full_name)));
    return g;
  }, [visibleEmps]);

  // ---- Row helpers ----
  const getRowDraft = (empId) => draft[empId] || { status: "Not working", time_in: "", time_out: "", break_minutes: 30, notes: "", entry_id: null, dirty: false };

  const rowHours = (d) => d.status === "Working" ? diffHours(d.time_in, d.time_out, d.break_minutes) : 0;

  const rowSaveStatus = (empId) => {
    const d = getRowDraft(empId);
    if (d.dirty) return "dirty";
    if (d.status === "Working") {
      if (!d.time_in || !d.time_out) return "missing";
      const h = diffHours(d.time_in, d.time_out, d.break_minutes);
      if (h > 12) return "needs_review";
      if (h >= 6 && Number(d.break_minutes) < 30) return "needs_review";
      if (d.entry_id) return "saved";
      return "missing";
    }
    if (d.status === "Not working") return "empty";
    return "saved";
  };

  const totalDirty = useMemo(() => Object.values(draft).filter((d) => d?.dirty).length, [draft]);

  // ---- Mutators ----
  const setRow = (empId, patch) => setDraft((prev) => ({ ...prev, [empId]: { ...getRowDraft(empId), ...patch, dirty: true } }));

  const applyBulk = (ids, patch) => {
    setDraft((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = { ...getRowDraft(id), ...patch, dirty: true }; });
      return next;
    });
  };

  // ---- Quick actions ----
  const setDateToday = () => setDate(todayStr());
  const setDateYesterday = () => setDate(addDays(todayStr(), -1));
  const weekJump = (delta) => setDate(addDays(date, delta));

  const targetsForQuickAction = () => selected.size > 0 ? Array.from(selected) : visibleEmps.map((e) => e.id);

  const quickSameAsYesterday = async () => {
    const yd = addDays(date, -1);
    try {
      const r = await api.get("/time-entries", { params: { start: yd, end: yd } });
      const map = {}; for (const e of r.data) map[e.employee_id] = e;
      const ids = targetsForQuickAction();
      let applied = 0;
      setDraft((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          const src = map[id];
          if (!src) return;
          next[id] = { ...getRowDraft(id), status: "Working", time_in: src.time_in, time_out: src.time_out, break_minutes: src.break_minutes, notes: src.notes || "", dirty: true };
          applied++;
        });
        return next;
      });
      toast.success(`${applied} rows copied from yesterday`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickMark = (status) => {
    const ids = targetsForQuickAction();
    applyBulk(ids, { status, time_in: "", time_out: "", notes: status === "Absent" ? "Absent" : (status === "On Holiday" ? "Annual leave" : (status === "Sick Leave" ? "" : "")) });
    toast.success(`${ids.length} rows set to ${status}`);
  };

  const quickNoHoursToday = () => {
    const ids = targetsForQuickAction();
    applyBulk(ids, { status: "Not working", time_in: "", time_out: "" });
    toast.success(`${ids.length} rows cleared`);
  };

  const applyPreset = (key) => {
    const p = presets[key];
    if (!p) return;
    const ids = targetsForQuickAction();
    if (ids.length === 0) return toast.error("No employees to apply preset to");
    applyBulk(ids, { status: "Working", time_in: p.time_in, time_out: p.time_out, break_minutes: Number(p.break_minutes || 0) });
    toast.success(`${p.label} applied to ${ids.length} row${ids.length === 1 ? "" : "s"}`);
  };

  const applyPresetToEmployee = (empId, key) => {
    const p = presets[key];
    if (!p) return;
    setRow(empId, { status: "Working", time_in: p.time_in, time_out: p.time_out, break_minutes: Number(p.break_minutes || 0) });
  };

  const quickCopyToSelected = () => {
    // Use first selected row as the source shift (or first visible if none selected)
    const list = selected.size > 0 ? Array.from(selected) : visibleEmps.map((e) => e.id);
    if (list.length < 2) return toast.error("Select at least 2 employees — first becomes source, rest receive");
    const src = getRowDraft(list[0]);
    if (!src.time_in || !src.time_out) return toast.error("Source row has no start/finish time");
    const dest = list.slice(1);
    applyBulk(dest, { status: "Working", time_in: src.time_in, time_out: src.time_out, break_minutes: src.break_minutes, notes: src.notes });
    toast.success(`Copied ${src.time_in}–${src.time_out} to ${dest.length} rows`);
  };

  const [carrying, setCarrying] = useState(false);
  const carryLastWeekForward = async () => {
    const prevWs = addDays(weekStart, -7);
    const msg = `Copy every APPROVED entry from week of ${prevWs} into the matching weekdays of ${weekStart}?\n\nRows that already have an entry for that date will be skipped.${selected.size > 0 ? `\n\nOnly ${selected.size} selected employees will be carried.` : ""}`;
    if (!window.confirm(msg)) return;
    setCarrying(true);
    try {
      const body = { target_week_start: weekStart, only_approved: true };
      if (selected.size > 0) body.employee_ids = Array.from(selected);
      const { data } = await api.post("/time-entries/carry-week", body);
      const sk = data.skipped_existing?.length || 0;
      const fl = data.failed?.length || 0;
      toast.success(`${data.created_count} entries carried forward from ${data.source_week_start}${sk ? ` · ${sk} skipped (already logged)` : ""}${fl ? ` · ${fl} failed` : ""}`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCarrying(false); }
  };

  // ---- Save ----
  const saveRow = async (empId) => {
    const d = getRowDraft(empId);
    if (!d.dirty) return { skipped: true };
    try {
      if (d.status === "Working") {
        if (!d.time_in || !d.time_out) return { error: "Missing times" };
        const body = { time_in: d.time_in, time_out: d.time_out, break_minutes: Number(d.break_minutes || 0), notes: d.notes || undefined };
        if (d.entry_id) {
          await api.patch(`/time-entries/${d.entry_id}`, body);
        } else {
          const r = await api.post("/time-entries", { employee_id: empId, date, ...body });
          d.entry_id = r.data.id;
        }
      } else if (d.status === "Absent") {
        // Record as 0-hour time entry with a note
        if (d.entry_id) {
          await api.patch(`/time-entries/${d.entry_id}`, { time_in: "00:00", time_out: "00:00", break_minutes: 0, notes: d.notes || "Absent" });
        } else {
          try {
            const r = await api.post("/time-entries", { employee_id: empId, date, time_in: "00:00", time_out: "00:00", break_minutes: 0, notes: d.notes || "Absent" });
            d.entry_id = r.data.id;
          } catch (e) {
            // accept soft failure if backend rejects zero-hour entries
            return { error: formatApiError(e) };
          }
        }
      } else if (d.status === "On Holiday") {
        await api.post("/holidays/entries", { employee_id: empId, date, days: 1, notes: d.notes || "Annual leave" });
      } else if (d.status === "Sick Leave") {
        await api.post("/sick-leave", { employee_id: empId, start_date: date, end_date: date, days: 1, type: "statutory", reason: d.notes || undefined });
      } else if (d.status === "Not working") {
        if (d.entry_id) await api.delete(`/time-entries/${d.entry_id}`);
        d.entry_id = null;
      }
      setDraft((prev) => ({ ...prev, [empId]: { ...getRowDraft(empId), dirty: false, entry_id: d.entry_id } }));
      return { ok: true };
    } catch (e) {
      return { error: formatApiError(e) };
    }
  };

  const saveAll = async () => {
    const ids = Object.keys(draft).filter((id) => draft[id]?.dirty);
    if (ids.length === 0) return toast("Nothing to save");
    let ok = 0, fail = 0;
    for (const id of ids) {
      const r = await saveRow(id);
      if (r?.ok) ok++; else if (r?.error) fail++;
    }
    toast[fail ? "warning" : "success"](`Saved ${ok} rows${fail ? ` · ${fail} failed` : ""}`);
    loadAll();
  };

  const clearChanges = () => {
    if (totalDirty === 0) return;
    if (!window.confirm(`Discard ${totalDirty} unsaved changes?`)) return;
    // Reset every row to server state
    setDraft((prev) => {
      const next = {};
      for (const emp of employees) {
        const te = serverEntries[emp.id];
        next[emp.id] = te
          ? { status: "Working", time_in: te.time_in, time_out: te.time_out, break_minutes: te.break_minutes, notes: te.notes || "", entry_id: te.id, dirty: false }
          : { status: "Not working", time_in: "", time_out: "", break_minutes: 30, notes: "", entry_id: null, dirty: false };
      }
      return next;
    });
  };

  // ---- Summary panel ----
  const currentSummaryEmp = useMemo(() => employees.find((e) => e.id === summaryEmpId) || employees[0], [employees, summaryEmpId]);
  const currentWeeklyRow = useMemo(() => weeklyRows.find((r) => r.employee.id === currentSummaryEmp?.id), [weeklyRows, currentSummaryEmp]);

  const summaryFigures = useMemo(() => {
    if (!currentSummaryEmp) return null;
    const contracted = Number(currentSummaryEmp.contracted_weekly_hours ?? currentSummaryEmp.standard_weekly_hours ?? 40);
    const loggedSoFar = currentWeeklyRow ? Number(currentWeeklyRow.total_hours) : 0;
    const thisEntry = (draft[currentSummaryEmp.id]?.dirty && draft[currentSummaryEmp.id]?.status === "Working")
      ? rowHours(draft[currentSummaryEmp.id]) - (serverEntries[currentSummaryEmp.id]?.total_hours || 0)
      : 0;
    const after = Math.round((loggedSoFar + thisEntry) * 100) / 100;
    const extra = Math.max(0, Math.round((after - contracted) * 100) / 100);
    const missing = Math.max(0, Math.round((contracted - after) * 100) / 100);
    const holHrs = (currentWeeklyRow ? Number(currentWeeklyRow.holiday_days) : 0) * Number(currentSummaryEmp.standard_daily_hours || 8);
    return { contracted, loggedSoFar, after, extra, missing, holHrs };
  }, [currentSummaryEmp, currentWeeklyRow, draft, serverEntries]);

  const warnings = useMemo(() => {
    const w = [];
    employees.forEach((emp) => {
      const d = draft[emp.id];
      if (!d) return;
      if (d.status === "Working" && d.time_in && d.time_out) {
        const h = diffHours(d.time_in, d.time_out, d.break_minutes);
        if (h > 10) w.push({ emp, msg: `${emp.full_name} has a long shift (${h}h) — consider a longer break.` });
        if (h >= 6 && Number(d.break_minutes) < 30) w.push({ emp, msg: `${emp.full_name} worked ${h}h with <30m break.` });
      }
      if (serverSick[emp.id] && d.status === "Working") {
        w.push({ emp, msg: `${emp.full_name} is marked Sick Leave — hours should not be logged.` });
      }
    });
    return w.slice(0, 6);
  }, [employees, draft, serverSick]);

  // ---- Selection helpers ----
  const toggleSelected = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllVisible = () => setSelected(new Set(visibleEmps.map((e) => e.id)));
  const clearSelection = () => setSelected(new Set());

  useEffect(() => { localStorage.setItem("qh:summaryCollapsed", summaryCollapsed ? "1" : "0"); }, [summaryCollapsed]);

  const toggleDeptCollapsed = (d) => setCollapsed((s) => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });

  // Filter drawer (mobile)
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Summary drawer (mobile)
  const [summaryOpen, setSummaryOpen] = useState(false);

  // ---- Render ----
  return (
    <div className="space-y-4 md:space-y-5 pb-24 md:pb-0" data-testid="quick-hours-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Fast logging</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 md:mt-2">Quick staff hours</h1>
          <p className="text-muted-foreground mt-1 text-sm hidden sm:block">Log today's staff hours in one place.</p>
        </div>
        <div className="flex items-end gap-2 md:gap-3 flex-wrap">
          <div className="hidden md:flex items-center gap-2 px-3 h-11 rounded-md border border-border bg-card">
            <span className="text-xs text-muted-foreground">Fast Entry Mode</span>
            <Switch checked={fastEntry} onCheckedChange={setFastEntry} data-testid="qh-fast-entry" />
            <Info className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
          {summaryCollapsed && (
            <Button variant="outline" className="hidden xl:flex h-11" onClick={() => setSummaryCollapsed(false)} data-testid="qh-summary-show">
              <CalendarClock className="w-4 h-4 mr-2" /> Show summary
            </Button>
          )}
          <Button onClick={saveAll} disabled={totalDirty === 0} className="h-11 px-4 md:px-5" data-testid="qh-save-all">
            <Save className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Save all</span> ({totalDirty})
          </Button>
          <Button variant="outline" onClick={clearChanges} className="h-11" data-testid="qh-clear">
            <RotateCcw className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Clear changes</span>
          </Button>
        </div>
      </div>

      {/* MOBILE: compact filter + summary triggers */}
      <div className="flex md:hidden gap-2">
        <Button variant="outline" className="flex-1 h-11 justify-between" onClick={() => setFiltersOpen(!filtersOpen)} data-testid="qh-m-filters">
          <span className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4" /> {fmtFullDate(date).replace(/, \d{4}$/, "")}
          </span>
          {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
        <Button variant="outline" className="h-11" onClick={() => setSummaryOpen(!summaryOpen)} data-testid="qh-m-summary">
          <CalendarClock className="w-4 h-4 mr-1" /> Summary
        </Button>
      </div>

      {/* Filter bar (always visible md+, collapsible on mobile) */}
      <Card className={`border-border shadow-none ${filtersOpen ? "" : "hidden md:block"}`}>
        <CardContent className="p-4 grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Date</Label>
            <div className="relative">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pr-9 h-10" data-testid="qh-date" />
              <Calendar className="w-4 h-4 absolute right-3 top-3 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div className="md:col-span-3">
            <Label>Week</Label>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-10 shrink-0" onClick={() => weekJump(-7)} data-testid="qh-week-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="flex-1 h-10 px-2 border border-input rounded-md flex items-center font-mono text-[11px] md:text-xs truncate">{fmtRange(weekStart)}</div>
              <Button variant="outline" size="icon" className="h-10 shrink-0" onClick={() => weekJump(7)} data-testid="qh-week-next"><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="outline" className="h-10 px-2 md:px-3 whitespace-nowrap text-xs md:text-sm" onClick={setDateToday}>This wk</Button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Department</Label>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="h-10" data-testid="qh-dept"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Employment type</Label>
            <Select value={emptype} onValueChange={setEmptype}>
              <SelectTrigger className="h-10" data-testid="qh-emptype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="full_time">Full time</SelectItem>
                <SelectItem value="part_time">Part time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input className="pl-9 h-10" placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="qh-search" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page-level quick actions */}
      <div className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible -mx-1 px-1 gap-2 items-center app-scroll pb-1" data-testid="qh-quick-actions">
        <Button variant="outline" size="sm" onClick={setDateToday} className="shrink-0"><Calendar className="w-3.5 h-3.5 mr-1.5" /> Today</Button>
        <Button variant="outline" size="sm" onClick={setDateYesterday} className="shrink-0"><Calendar className="w-3.5 h-3.5 mr-1.5" /> Yesterday</Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset("standard")} className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="qh-preset-standard">
          <Clock3 className="w-3.5 h-3.5 mr-1.5" /> {presets.standard?.label || "Usual"} · {presets.standard?.time_in}–{presets.standard?.time_out}
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset("early")} className="shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="qh-preset-early">
          <SunMedium className="w-3.5 h-3.5 mr-1.5" /> {presets.early?.label || "Early"} · {presets.early?.time_in}–{presets.early?.time_out}
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset("late")} className="shrink-0 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="qh-preset-late">
          <Moon className="w-3.5 h-3.5 mr-1.5" /> {presets.late?.label || "Late"} · {presets.late?.time_in}–{presets.late?.time_out}
        </Button>
        <Button variant="outline" size="sm" onClick={quickSameAsYesterday} className="shrink-0" data-testid="qh-same-yesterday"><Copy className="w-3.5 h-3.5 mr-1.5" /> Same as yesterday</Button>
        <Button variant="outline" size="sm" onClick={quickCopyToSelected} className="shrink-0" data-testid="qh-copy-to-sel"><ClipboardCopy className="w-3.5 h-3.5 mr-1.5" /> Copy to selected</Button>
        <Button variant="outline" size="sm" onClick={carryLastWeekForward} disabled={carrying} className="shrink-0 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="qh-carry-week">
          <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> {carrying ? "Carrying…" : "Carry last week"}
        </Button>
        <Button variant="outline" size="sm" onClick={quickNoHoursToday} className="shrink-0"><Ban className="w-3.5 h-3.5 mr-1.5" /> No hours today</Button>
        <Button variant="outline" size="sm" className="shrink-0 text-red-700 border-red-200 hover:bg-red-50" onClick={() => quickMark("Absent")} data-testid="qh-mark-absent"><CalendarX className="w-3.5 h-3.5 mr-1.5" /> Mark absent</Button>
        <Button variant="outline" size="sm" className="shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => quickMark("On Holiday")} data-testid="qh-mark-holiday"><Palmtree className="w-3.5 h-3.5 mr-1.5" /> Mark holiday</Button>
        <Button variant="outline" size="sm" className="shrink-0 text-violet-700 border-violet-200 hover:bg-violet-50" onClick={() => quickMark("Sick Leave")} data-testid="qh-mark-sick"><Stethoscope className="w-3.5 h-3.5 mr-1.5" /> Mark sick</Button>
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Badge variant="outline">{selected.size} selected</Badge>
            <Button variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>
          </div>
        )}
      </div>

      {/* Main layout: table grouped by dept + right summary panel */}
      <div className={`grid gap-5 ${summaryCollapsed ? "" : "xl:grid-cols-[minmax(0,1fr)_320px]"}`}>
        {/* LEFT: grouped tables */}
        <div className="space-y-5 min-w-0">
          {Object.entries(grouped).map(([deptName, list]) => {
            const isCollapsed = collapsed.has(deptName);
            return (
              <Card key={deptName} className="border-border shadow-none overflow-hidden" data-testid={`qh-dept-${deptName}`}>
                <button
                  type="button"
                  onClick={() => toggleDeptCollapsed(deptName)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/40 border-b border-border"
                >
                  <div className="flex items-center gap-2">
                    {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold">{deptName}</span>
                    <Badge variant="outline" className="text-xs">{list.length}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtFullDate(date)}</span>
                </button>
                {!isCollapsed && (
                  <>
                    {/* DESKTOP TABLE (md+) */}
                    <div className="hidden md:block overflow-x-auto app-scroll">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/30">
                          <tr className="text-left tiny-label">
                            <th className="px-3 py-3 w-8"><Checkbox checked={list.every((e) => selected.has(e.id)) && list.length > 0} onCheckedChange={(v) => {
                              setSelected((s) => { const n = new Set(s); list.forEach((e) => v ? n.add(e.id) : n.delete(e.id)); return n; });
                            }} /></th>
                            <th className="px-3 py-3">Employee</th>
                            <th className="px-3 py-3">Type</th>
                            <th className="px-3 py-3 text-right">Contracted hrs</th>
                            <th className="px-3 py-3">Status</th>
                            <th className="px-3 py-3">Start</th>
                            <th className="px-3 py-3">Finish</th>
                            <th className="px-3 py-3">Break</th>
                            <th className="px-3 py-3 text-right">Hours</th>
                            <th className="px-3 py-3">Notes</th>
                            <th className="px-3 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((emp) => {
                            const d = getRowDraft(emp.id);
                            const saveStatus = rowSaveStatus(emp.id);
                            const isWorking = d.status === "Working";
                            const hrs = rowHours(d);
                            return (
                              <tr key={emp.id} className={`border-t border-border ${selected.has(emp.id) ? "bg-muted/30" : ""}`} data-testid={`qh-row-${emp.employee_id}`}>
                                <td className="px-3 py-2 align-middle"><Checkbox checked={selected.has(emp.id)} onCheckedChange={() => toggleSelected(emp.id)} /></td>
                                <td className="px-3 py-2 align-middle">
                                  <div className="flex items-center gap-2 min-w-[200px]">
                                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">{initials(emp.full_name)}</div>
                                    <div>
                                      <div className="font-medium leading-tight">{emp.full_name}</div>
                                      {emp.department && (
                                        <div className="text-[11px] text-muted-foreground">{emp.department}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2 capitalize text-xs">{(emp.employment_type || "").replace("_", " ")}</td>
                                <td className="px-3 py-2 text-right font-mono text-xs">{(emp.contracted_weekly_hours ?? emp.standard_weekly_hours ?? 40).toFixed(2)}</td>
                                <td className="px-3 py-2">
                                  <Select value={d.status} onValueChange={(v) => setRow(emp.id, { status: v, ...(v !== "Working" ? { time_in: "", time_out: "" } : {}) })}>
                                    <SelectTrigger className={`h-9 w-32 ${toneFor(d.status)} font-medium`} data-testid={`qh-status-${emp.employee_id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.v}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-3 py-2">
                                  <TimeInput className="h-9 w-[5.5rem]" value={d.time_in || ""} disabled={!isWorking} onChange={(e) => setRow(emp.id, { time_in: e.target.value })} data-testid={`qh-in-${emp.employee_id}`} />
                                </td>
                                <td className="px-3 py-2">
                                  <TimeInput className="h-9 w-[5.5rem]" value={d.time_out || ""} disabled={!isWorking} onChange={(e) => setRow(emp.id, { time_out: e.target.value })} data-testid={`qh-out-${emp.employee_id}`} />
                                </td>
                                <td className="px-3 py-2">
                                  <Input type="number" min="0" className="h-9 w-20" value={d.break_minutes ?? 0} disabled={!isWorking} onChange={(e) => setRow(emp.id, { break_minutes: Number(e.target.value) })} data-testid={`qh-break-${emp.employee_id}`} />
                                </td>
                                <td className="px-3 py-2 text-right font-mono font-semibold" title={d.hours_source ? `Hours source: ${SOURCE_LABEL[d.hours_source] || d.hours_source}` : undefined}>
                                  {isWorking ? hrs.toFixed(2) : "—"}
                                  {d.hours_source && (
                                    <div className="text-[10px] font-normal text-muted-foreground" data-testid={`qh-source-${emp.employee_id}`}>
                                      {SOURCE_LABEL[d.hours_source] || d.hours_source}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2"><Input className="h-9 w-36" value={d.notes || ""} onChange={(e) => setRow(emp.id, { notes: e.target.value })} placeholder="—" /></td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className={SAVE_TONE[saveStatus]}>
                                    {saveStatus === "saved" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                    {saveStatus === "needs_review" && <AlertTriangle className="w-3 h-3 mr-1" />}
                                    {saveStatus === "missing" && <AlertTriangle className="w-3 h-3 mr-1" />}
                                    {saveStatus === "dirty" && <Circle className="w-3 h-3 mr-1 fill-current" />}
                                    {SAVE_LABEL[saveStatus]}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* MOBILE CARDS (<md) */}
                    <div className="md:hidden divide-y divide-border">
                      {list.map((emp) => {
                        const d = getRowDraft(emp.id);
                        const saveStatus = rowSaveStatus(emp.id);
                        const isWorking = d.status === "Working";
                        const hrs = rowHours(d);
                        const contracted = (emp.contracted_weekly_hours ?? emp.standard_weekly_hours ?? 40);
                        return (
                          <div key={emp.id} className={`p-3 ${selected.has(emp.id) ? "bg-muted/30" : ""}`} data-testid={`qh-mrow-${emp.employee_id}`}>
                            {/* Row 1: avatar + name + selection + save status */}
                            <div className="flex items-center gap-3">
                              <Checkbox checked={selected.has(emp.id)} onCheckedChange={() => toggleSelected(emp.id)} />
                              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">{initials(emp.full_name)}</div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium leading-tight truncate">{emp.full_name}</div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                  <span className="capitalize">{(emp.employment_type || "").replace("_", " ")}</span> · {contracted}h/wk
                                </div>
                              </div>
                              <Badge variant="outline" className={`${SAVE_TONE[saveStatus]} whitespace-nowrap text-[10px]`}>
                                {SAVE_LABEL[saveStatus]}
                              </Badge>
                            </div>

                            {/* Row 2: status + hours summary */}
                            <div className="flex items-center gap-2 mt-3">
                              <Select value={d.status} onValueChange={(v) => setRow(emp.id, { status: v, ...(v !== "Working" ? { time_in: "", time_out: "" } : {}) })}>
                                <SelectTrigger className={`h-9 flex-1 ${toneFor(d.status)} font-medium`} data-testid={`qh-mstatus-${emp.employee_id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.v}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <div className="h-9 px-3 rounded-md border border-input bg-muted/40 flex items-center font-mono font-semibold text-sm min-w-[80px] justify-end">
                                {isWorking ? `${hrs.toFixed(2)}h` : "—"}
                              </div>
                            </div>

                            {/* Row 3: time inputs — ONLY when Working */}
                            {isWorking && (
                              <>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Shift times</div>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid={`qh-mpreset-${emp.employee_id}`}>
                                        <MoreVertical className="w-3.5 h-3.5 mr-1" /> Preset
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuLabel>Apply shift</DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => applyPresetToEmployee(emp.id, "standard")} data-testid={`qh-mpreset-${emp.employee_id}-standard`}>
                                        <Clock3 className="w-4 h-4 mr-2 text-emerald-600" />
                                        <div className="flex flex-col">
                                          <span className="font-medium">{presets.standard.label}</span>
                                          <span className="text-[10px] text-muted-foreground">{presets.standard.time_in}–{presets.standard.time_out} · {presets.standard.break_minutes}m break</span>
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => applyPresetToEmployee(emp.id, "early")} data-testid={`qh-mpreset-${emp.employee_id}-early`}>
                                        <SunMedium className="w-4 h-4 mr-2 text-indigo-600" />
                                        <div className="flex flex-col">
                                          <span className="font-medium">{presets.early.label}</span>
                                          <span className="text-[10px] text-muted-foreground">{presets.early.time_in}–{presets.early.time_out} · {presets.early.break_minutes}m break</span>
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => applyPresetToEmployee(emp.id, "late")} data-testid={`qh-mpreset-${emp.employee_id}-late`}>
                                        <Moon className="w-4 h-4 mr-2 text-amber-600" />
                                        <div className="flex flex-col">
                                          <span className="font-medium">{presets.late.label}</span>
                                          <span className="text-[10px] text-muted-foreground">{presets.late.time_in}–{presets.late.time_out} · {presets.late.break_minutes}m break</span>
                                        </div>
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-1">
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Start</div>
                                    <TimeInput className="h-10 text-base w-full" value={d.time_in || ""} onChange={(e) => setRow(emp.id, { time_in: e.target.value })} data-testid={`qh-min-${emp.employee_id}`} />
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Finish</div>
                                    <TimeInput className="h-10 text-base w-full" value={d.time_out || ""} onChange={(e) => setRow(emp.id, { time_out: e.target.value })} data-testid={`qh-mout-${emp.employee_id}`} />
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Break (m)</div>
                                    <Input type="number" min="0" className="h-10 text-base" value={d.break_minutes ?? 0} onChange={(e) => setRow(emp.id, { break_minutes: Number(e.target.value) })} data-testid={`qh-mbreak-${emp.employee_id}`} />
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Row 4: notes */}
                            <Input className="h-9 mt-2" value={d.notes || ""} onChange={(e) => setRow(emp.id, { notes: e.target.value })} placeholder="Notes…" />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </Card>
            );
          })}
          {!loading && Object.keys(grouped).length === 0 && (
            <Card className="border-border shadow-none p-10 text-center text-muted-foreground">No employees match your filters.</Card>
          )}
        </div>

        {/* RIGHT: Weekly Summary panel — visible on xl desktop unless collapsed; on mobile only when summaryOpen */}
        <aside className={`space-y-4 xl:sticky xl:top-4 xl:self-start ${summaryOpen ? "" : "hidden xl:block"} ${summaryCollapsed ? "xl:hidden" : ""}`}>
          <Card className="border-border shadow-none">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold">Weekly summary</div>
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setSummaryCollapsed(true)} data-testid="qh-summary-collapse" title="Hide weekly summary">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Select value={summaryEmpId || currentSummaryEmp?.id || ""} onValueChange={setSummaryEmpId}>
                <SelectTrigger data-testid="qh-summary-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {summaryFigures && currentSummaryEmp && (
                <>
                  <div className="text-xs text-muted-foreground">Week: {fmtRange(weekStart)}</div>
                  <div className="space-y-1.5 pt-1">
                    <Row label="Contracted hours" val={summaryFigures.contracted.toFixed(2)} />
                    <Row label="Hours logged (so far)" val={summaryFigures.loggedSoFar.toFixed(2)} />
                    <Row label="After this entry" val={summaryFigures.after.toFixed(2)} bold />
                    <div className="h-px bg-border my-1" />
                    <Row label="Extra hours" val={summaryFigures.extra.toFixed(2)} tone={summaryFigures.extra > 0 ? "amber" : "muted"} />
                    <Row label="Missing hours" val={summaryFigures.missing.toFixed(2)} tone={summaryFigures.missing > 0 ? "red" : "muted"} />
                    <Row label="Holiday hours" val={summaryFigures.holHrs.toFixed(2)} tone="emerald" />
                    <Row label="Sick leave hours" val="0.00" tone="violet" />
                  </div>
                  <Alert className="mt-2 border-amber-200 bg-amber-50 text-amber-800">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertTitle>{summaryFigures.missing > 0 ? "Behind target" : summaryFigures.extra > 0 ? "Over target" : "This week is on target"}</AlertTitle>
                    <AlertDescription className="text-xs">{summaryFigures.missing > 0 ? `${summaryFigures.missing.toFixed(2)}h short of contracted` : summaryFigures.extra > 0 ? `${summaryFigures.extra.toFixed(2)}h over contracted` : "No issues found"}</AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-border shadow-none">
            <CardContent className="p-5 space-y-2">
              <div className="font-semibold">Warnings</div>
              {warnings.length === 0 && <div className="text-xs text-muted-foreground">All clear · no issues detected.</div>}
              {warnings.map((w, i) => (
                <Alert key={i} className="border-amber-200 bg-amber-50 text-amber-900" data-testid={`qh-warning-${i}`}>
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription className="text-xs">{w.msg}</AlertDescription>
                </Alert>
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-xs text-muted-foreground pt-2">
        <LegendDot tone="emerald" label="Saved" hint="All good" />
        <LegendDot tone="amber" label="Needs review" hint="Check entry" />
        <LegendDot tone="red" label="Missing / invalid" hint="Action required" />
        <LegendDot tone="slate" label="Not working today" hint="No hours logged" />
      </div>

      {/* Unsaved banner */}
      {totalDirty > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-md border border-amber-200 bg-amber-50 text-amber-900 shadow-lg px-4 py-2 flex items-center gap-3" data-testid="qh-unsaved-banner">
          <Info className="w-4 h-4" />
          <span className="text-sm">Unsaved changes exist. Don't forget to <strong>Save all</strong>.</span>
          <Button size="sm" className="h-8" onClick={saveAll}>Save all ({totalDirty})</Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, val, bold, tone }) {
  const toneCls = tone === "amber" ? "text-amber-700" : tone === "red" ? "text-red-700" : tone === "emerald" ? "text-emerald-700" : tone === "violet" ? "text-violet-700" : "text-foreground";
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${bold ? "font-bold" : ""} ${toneCls}`}>{val}</span>
    </div>
  );
}

function LegendDot({ tone, label, hint }) {
  const bg = tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : tone === "red" ? "bg-red-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${bg}`} />
      <span className="font-medium text-foreground">{label}</span>
      <span>· {hint}</span>
    </div>
  );
}
